import { SelectChangeEvent, ToggleButton, ToggleButtonGroup } from "@mui/material";
import Box from "@mui/material/Box";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { FilterProvider } from "../filters/filter-context";
import FullTimeShifts from "../full-time-shifts";
import {
	getFullTimeShiftsFilters,
	resetShiftsMeta,
	setSelectedLocation as setShiftSelectedLocation
} from "../full-time-shifts/store/full-time-shifts-slice";
import WorkerScheduler from "../full-time-worker-scheduler";
import AttributesAdmin from "./attributes-admin";
import { ATTRIBUTES, HOURS, LABOR_REQUIREMENTS, LINE_ACTIVITIES, PRODUCTION_PLAN, ROSTER, SETTINGS_TABS, SHIFT_ROSTER, SHIFTS, WORKERS } from "./constants"; // eslint-disable-line sort-imports
import FullTimeSchedulerHeader from "./header";
import HoursReportPage from "./hours-report";
import LaborStandards from "./labor-requirements";
import LineActivities from "./line-activities";
import { planner } from "./planner-ui";
import ProductionPlan from "./production-plan";
import Roster from "./roster";
import WorkPhasesAdmin from "./work-phases-admin";

// The Setup tab holds two per-company panels. Same segmented-control recipe as
// the roster's week/day toggle so the two switches read as one control type.
type SetupPanel = "attributes" | "work-phases"

const setupToggleSx = {
	bgcolor: planner.surface,
	border: `${planner.borderWidth}px solid ${planner.hairlineStrong}`,
	borderRadius: `${planner.radiusSm}px`,
	p: 0.25,
	gap: 0.25,
	"& .MuiToggleButton-root": {
		...planner.type.bodyStrong,
		fontFamily: planner.fontUi,
		border: 0,
		borderRadius: `${planner.radiusXs}px`,
		px: 1.5,
		py: 0.4,
		textTransform: "none",
		color: planner.inkSecondary,
		"&.Mui-selected": {
			bgcolor: planner.accentSoft,
			color: planner.accent,
			"&:hover": {
				bgcolor: planner.accentSoft,
			},
		},
	},
};

// Companies that run the production planner land on Roster; everyone else lands
// on the crew schedule (Shifts). Comma-separated company ids from the build env,
// or "*" to land every company on the planner (try.wilya.com does this).
const PLANNER_COMPANY_IDS_RAW = process.env.REACT_APP_PLANNER_COMPANY_IDS ?? "";
const ALL_COMPANIES_ON_PLANNER = PLANNER_COMPANY_IDS_RAW.trim() === "*";
const PLANNER_COMPANY_IDS = PLANNER_COMPANY_IDS_RAW
	.split(",")
	.map((id) => Number(id.trim()))
	.filter((id) => Number.isInteger(id) && id > 0);

// Shifts, Workers and Roster are hidden from the bar; everyone lands on the
// Shift Roster. (The planner-company list above is kept for when they return.)
const landingTabFor = (_companyId?: number) => SHIFT_ROSTER; // eslint-disable-line @typescript-eslint/no-unused-vars

const SchedulerTabs = () => {
	const currentUserId = useSelector((state: any) => state.user?.userData?.id);
	const companyId = useSelector((state: any) => state.user?.userData?.companyId);

	const dispatch = useDispatch();
	const [activeTab, setActiveTab] = useState(() => landingTabFor(companyId));
	// userData can arrive after the first render; re-pick the landing tab once
	// per company so a planner company is not left on the crew schedule.
	const [landingCompanyId, setLandingCompanyId] = useState(companyId);
	useEffect(() => {
		if (companyId && companyId !== landingCompanyId) {
			setLandingCompanyId(companyId);
			setActiveTab(landingTabFor(companyId));
		}
	}, [companyId, landingCompanyId]);
	// Defaults to the panel that used to be this tab, so nothing moves for
	// existing users.
	const [setupPanel, setSetupPanel] = useState<SetupPanel>("attributes");

	// Load filter data (including locations) when component mounts
	useEffect(() => {
		dispatch(getFullTimeShiftsFilters() as any);
	}, [dispatch]);

	const selectedLocation = useSelector(
		(state: any) => state.fullTimeShifts.selectedLocation
	);

	const allLocations = useSelector(
		(state: any) => state.fullTimeShifts?.filterData?.allLocations
	);

	const locationsWithFTSEnabled = useSelector(
		(state: any) => state.user?.userData?.ftSchedulerEnabledLocations
	);

	const ftSchedulerEnabledLocations = allLocations.filter(
		(location: { id: number }) => locationsWithFTSEnabled.includes(location.id)
	);

	const handleLocationChange = (
		event: SelectChangeEvent<{ value: string }>
	) => {
		dispatch(setShiftSelectedLocation(Number(event.target.value)));
	};

	useEffect(() => {
		if (activeTab === WORKERS) {
			// Reset Shifts Meta when switching to Workers tab
			dispatch(resetShiftsMeta());
		}
	}, [activeTab, dispatch]);

	useEffect(() => {
		try {
			if (ftSchedulerEnabledLocations.length > 0) {
				if (!ftSchedulerEnabledLocations?.map(
					(location: { id: number }) => location.id
				).includes(selectedLocation)) {
					// Reset selected location to the first available location on refresh
					// If due to cache selected location is having wrong value
					const singleLocation = ftSchedulerEnabledLocations[0];
					dispatch(setShiftSelectedLocation(singleLocation.id));
				}
			}
		} catch (e) {
			// Do nothing
		}
	}, [dispatch, ftSchedulerEnabledLocations, selectedLocation]);

	return (
		<Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "visible" }}>
			<FullTimeSchedulerHeader
				activeTab={activeTab}
				setActiveTab={setActiveTab}
				selectedLocation={selectedLocation}
				handleLocationChange={handleLocationChange}
				allLocations={allLocations}
				ftSchedulerEnabledLocations={ftSchedulerEnabledLocations}
			/>
			{/*
			  minWidth 0 is load-bearing. A flex item defaults to min-width:auto, so
			  this row refused to shrink below its content — the Work Plan day grid
			  and the 2W/4W board are deliberately wider than the viewport and scroll
			  inside their own containers, but that only works if every flex ancestor
			  is allowed to be narrower than them. Without it this wrapper grew to the
			  full grid width and pushed the page header, horizon switcher and
			  Campaigns/Upload actions off-screen with no visible scrollbar.
			*/}
			<Box
				sx={{
					display: "flex",
					flexDirection: "row",
					height: "100%",
					overflow: "hidden",
					minWidth: 0,
				}}
			>
				{activeTab === WORKERS && (
					<FilterProvider filterKey={`worker-scheduler-${currentUserId}`}>
						<WorkerScheduler />
					</FilterProvider>
				)}
				{activeTab === SHIFTS && (
					<FilterProvider filterKey={`full-time-scheduler-${currentUserId}`}>
						<FullTimeShifts />
					</FilterProvider>
				)}
				{activeTab === ROSTER && (
					<Roster />
				)}
				{activeTab === SHIFT_ROSTER && (
					<Roster layout="crew" />
				)}
				{activeTab === PRODUCTION_PLAN && (
					<ProductionPlan />
				)}
				{SETTINGS_TABS.includes(activeTab) && (
					<Box sx={{ flex: 1, minWidth: 0, height: "100%", display: "flex", flexDirection: "column", overflow: "hidden", bgcolor: planner.canvas }}>
						<Box sx={{ px: 3, pt: 2.5, flexShrink: 0 }}>
							<ToggleButtonGroup
								value={activeTab}
								exclusive
								size="small"
								aria-label="settings"
								onChange={(_event, tab: string | null) => {
									if (tab !== null) setActiveTab(tab);
								}}
								sx={setupToggleSx}
							>
								<ToggleButton value={LABOR_REQUIREMENTS} aria-label="standards">
									Standards
								</ToggleButton>
								<ToggleButton value={LINE_ACTIVITIES} aria-label="cleaning and changeover">
									Cleaning &amp; changeover
								</ToggleButton>
								<ToggleButton value={HOURS} aria-label="hours">
									Hours
								</ToggleButton>
								<ToggleButton value={ATTRIBUTES} aria-label="setup">
									Setup
								</ToggleButton>
							</ToggleButtonGroup>
						</Box>
						<Box sx={{ display: "flex", flex: 1, minHeight: 0, minWidth: 0 }}>
							{activeTab === LABOR_REQUIREMENTS && <LaborStandards />}
							{activeTab === LINE_ACTIVITIES && <LineActivities />}
							{activeTab === HOURS && <HoursReportPage />}
							{activeTab === ATTRIBUTES && (
								<Box sx={{ flex: 1, minWidth: 0, height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
									<Box sx={{ px: 3, pt: 2, flexShrink: 0 }}>
										<ToggleButtonGroup
											value={setupPanel}
											exclusive
											size="small"
											aria-label="setup panel"
											onChange={(_event, panel: SetupPanel | null) => {
												if (panel !== null) {
													setSetupPanel(panel);
												}
											}}
											sx={setupToggleSx}
										>
											<ToggleButton value="attributes" aria-label="order attributes">
												Order attributes
											</ToggleButton>
											<ToggleButton value="work-phases" aria-label="work phases">
												Work phases
											</ToggleButton>
										</ToggleButtonGroup>
									</Box>
									<Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
										{setupPanel === "attributes" ? <AttributesAdmin /> : <WorkPhasesAdmin />}
									</Box>
								</Box>
							)}
						</Box>
					</Box>
				)}
			</Box>
		</Box>
	);
};

export default SchedulerTabs;
